import {
  CardGalleryItem,
  CardGalleryItemProps,
  Image,
  ImageProps,
} from '@wix/design-system';
import React, { cloneElement, isValidElement, ReactNode } from 'react';
import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { GridState, ItemActionsState } from '../../state';
import { observer } from 'mobx-react-lite';
import { toGridItemActions } from './toGridItemActions';
import { CollectionActionModal } from '../CollectionActionModal';
import { ActionsCell } from '../ActionsCell';

export interface GridCardItemProps<T, F extends FiltersMap> {
  children?: ReactNode;
  state: GridState<T, F>;
  keyedItem: KeyedItem<T>;
  index: number;
  rowIndex?: number;
  columnIndex?: number;
  actionsCell?: ActionsCell<T, F>;
  preset?: 'full' | 'title' | 'empty';
  renderItem?: (item: T, index: number) => Partial<CardGalleryItemProps>;
  draggable?: boolean;
  droppable?: boolean;
  dragging?: boolean;
  dragHandleProps?: CardGalleryItemProps['dragHandleProps'];
  domRef?: (node: HTMLElement | null) => void;
}

function _GridCardItem<T, F extends FiltersMap>(
  props: GridCardItemProps<T, F>,
) {
  const {
    state,
    keyedItem,
    index,
    actionsCell,
    preset,
    renderItem,
    draggable,
    droppable,
    dragging,
    dragHandleProps,
  } = props;
  const [itemActionsState] = React.useState(
    () =>
      new ItemActionsState({
        collection: state.collection,
        keyedItem,
        fade: 'idle',
      }),
  );

  const { item } = keyedItem;

  const {
    collection,
    calcGridProps: { cardFooterSize },
  } = state;
  const { itemName } = collection;

  const renderProps = renderItem?.(item, index) ?? {};

  const { backgroundImageNode } = renderProps;
  const imagePlacement = renderProps.imagePlacement ?? state.imagePlacement;

  const { primaryAction, settingsMenu } = toGridItemActions({
    actionsCell,
    item,
    index,
    gridState: state,
    keyedItem,
    itemActionsState,
    imagePlacement,
  });

  const textProps = (() => {
    if (preset === 'empty') {
      return {
        title: undefined,
        subtitle: undefined,
      };
    }

    return {
      title: renderProps?.title || itemName(item) || <>&#8203;</>,
      subtitle:
        preset === 'full' ? renderProps?.subtitle || <>&#8203;</> : undefined,
    };
  })();

  return (
    <>
      <CardGalleryItem
        tabIndex={0}
        dataHook="grid-card-item"
        {...renderProps}
        size={cardFooterSize}
        imagePlacement={imagePlacement}
        backgroundImageNode={
          backgroundImageNode == null ? (
            <Image borderRadius={0} height="100%" />
          ) : isValidElement<ImageProps>(backgroundImageNode) ? (
            cloneElement(backgroundImageNode, {
              borderRadius: 0,
            })
          ) : (
            backgroundImageNode
          )
        }
        {...textProps}
        primaryActionProps={{
          ...(primaryAction ?? renderProps?.primaryActionProps),
        }}
        settingsMenu={settingsMenu ?? renderProps.settingsMenu}
        draggable={draggable}
        dragging={dragging}
        droppable={droppable}
        dragHandleProps={dragHandleProps}
      />
      <CollectionActionModal
        state={itemActionsState.confirmModal}
        {...itemActionsState.confirmModal.value}
      />
    </>
  );
}

export const GridCardItem = observer(_GridCardItem);
